import * as React from "react";
import { Redirect } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { resetPassword, sendPasswordResetOtp } from "api/auth";
import { getApiErrorCode, getApiErrorMessage } from "api/apiError";
import {
  Alert,
  Button,
  FormField,
  OtpInput,
  PasswordChecklist,
  PasswordInput,
} from "Components/ui";
import { formatCountdown, useCountdown } from "hooks/useCountdown";
import { meetsPasswordPolicy, PASSWORD_POLICY_MESSAGE } from "utils/passwordPolicy";
import { StepHeader } from "./AuthLayout";
import { useAuthFlow } from "./data";
import { AUTH_PATHS } from "./types";

const CODE_LENGTH = 6;
const RESEND_SECONDS = 30;

const schema = z
  .object({
    otp: z.string().length(CODE_LENGTH, `Enter all ${CODE_LENGTH} digits of the code`),
    newPassword: z.string().refine(meetsPasswordPolicy, PASSWORD_POLICY_MESSAGE),
    confirmPassword: z.string().min(1, "Re-enter the new password"),
  })
  .refine((values) => values.newPassword === values.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords don't match",
  });

type ResetValues = z.infer<typeof schema>;

const ResetStep = () => {
  const flow = useAuthFlow();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [status, setStatus] = React.useState<string | null>(null);
  const [resending, setResending] = React.useState(false);
  const [cooldown, restartCooldown] = useCountdown(RESEND_SECONDS);
  const {
    control,
    register,
    handleSubmit,
    watch,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({
    resolver: zodResolver(schema),
    defaultValues: { otp: "", newPassword: "", confirmPassword: "" },
  });
  const newPassword = watch("newPassword");

  if (!flow.userName) {
    return <Redirect to={AUTH_PATHS.forgot} />;
  }

  const onSubmit = async (values: ResetValues) => {
    setServerError(null);
    setStatus(null);
    try {
      await resetPassword({
        userName: flow.userName,
        newPassword: values.newPassword,
        otp: values.otp,
      });
      flow.backToSignIn({
        tone: "success",
        title: "Password updated",
        message: "Sign in with your new password.",
      });
    } catch (err) {
      if (getApiErrorCode(err) === "AUTH_OTP_INVALID") {
        setError("otp", {
          message:
            "The verification code is incorrect or has expired. Request a new code and try again.",
        });
        setValue("otp", "");
        return;
      }
      setServerError(
        getApiErrorMessage(err, "We couldn't update your password. Please try again.")
      );
    }
  };

  const resend = async () => {
    if (cooldown > 0 || resending) {
      return;
    }
    setResending(true);
    setServerError(null);
    try {
      const res = await sendPasswordResetOtp(flow.userName);
      restartCooldown(RESEND_SECONDS);
      setValue("otp", "");
      setStatus(
        res.message
          ? "If this username matches an active account, a new code is on its way. Earlier codes no longer work."
          : "A new code has been sent. Earlier codes no longer work."
      );
    } catch (err) {
      setServerError(
        getApiErrorMessage(err, "We couldn't send a new code. Please try again in a moment.")
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <StepHeader
        back={{ label: "Back to sign in", onClick: () => flow.backToSignIn() }}
        step={{ current: 2, total: 2, label: "Reset password" }}
        title="Set a new password"
        description={
          flow.resetSentMessage
            ? "If this username matches an active account, we've sent a verification code to its WhatsApp number. Enter it below and choose a new password."
            : "Enter the verification code from WhatsApp and choose a new password."
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {serverError && <Alert tone="danger">{serverError}</Alert>}

        <FormField label="Reset code" error={errors.otp?.message}>
          <Controller
            control={control}
            name="otp"
            render={({ field }) => (
              <OtpInput
                ref={field.ref}
                value={field.value}
                onChange={field.onChange}
                length={CODE_LENGTH}
                autoFocus
              />
            )}
          />
        </FormField>

        {status && (
          <p role="status" className="text-sm text-success-text">
            {status}
          </p>
        )}

        <FormField label="New password" error={errors.newPassword?.message}>
          <PasswordInput autoComplete="new-password" {...register("newPassword")} />
        </FormField>
        <PasswordChecklist password={newPassword} />

        <FormField label="Confirm new password" error={errors.confirmPassword?.message}>
          <PasswordInput autoComplete="new-password" {...register("confirmPassword")} />
        </FormField>

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Update password
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          Didn't get a code?{" "}
          {cooldown > 0 ? (
            <span className="tabular-nums">Resend in {formatCountdown(cooldown)}</span>
          ) : (
            <Button variant="link" onClick={resend} loading={resending} className="align-baseline">
              Resend code
            </Button>
          )}
        </p>
      </form>
    </>
  );
};

export default ResetStep;
